import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Save as SaveIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { companySettingsService, CompanySettings as CompanySettingsType } from '../services/companySettingsService';

const CompanySettings: React.FC = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Partial<CompanySettingsType>>({
    company_name: '',
    company_address: ''
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  // Redirect if not admin
  if (!isAdmin()) {
    return <Navigate to="/" replace />;
  }

  const fetchCompanySettings = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const data = await companySettingsService.getCompanySettings();

      if (data) {
        setSettings({
          id: data.id,
          company_name: data.company_name || '',
          company_address: data.company_address || ''
        });
      } else {
        // Initialize with empty values if no settings exist
        setSettings({
          company_name: '',
          company_address: ''
        });
      }
    } catch (error) {
      console.error('Error in fetchCompanySettings:', error);
      setErrorMessage('Failed to load company settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CompanySettingsType, value: string) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');

      if (!settings.company_name?.trim()) {
        setErrorMessage('Company name is required');
        return;
      }

      if (!settings.company_address?.trim()) {
        setErrorMessage('Company address is required');
        return;
      }

      let result;
      if (settings.id) {
        // Update existing settings
        result = await companySettingsService.updateCompanySettings({
          id: settings.id,
          company_name: settings.company_name?.trim() || '',
          company_address: settings.company_address?.trim() || ''
        });
      } else {
        // Create new settings
        result = await companySettingsService.createCompanySettings({
          company_name: settings.company_name?.trim() || '',
          company_address: settings.company_address?.trim() || ''
        });
      }

      if (!result) {
        setErrorMessage('Failed to save company settings. Please check your input and try again.');
      } else {
        setSettings({
          id: result.id,
          company_name: result.company_name || '',
          company_address: result.company_address || ''
        });
        setSuccessMessage('Company settings saved successfully!');
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (error) {
      console.error('Error saving company settings:', error);
      setErrorMessage('An error occurred while saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BusinessIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Company Settings
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Configure your company information that will appear on payslips and other documents.
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      <Card elevation={2}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Company Information
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Company Name
                </Typography>
              </Box>
              <TextField
                fullWidth
                value={settings.company_name || ''}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                placeholder="Enter your company name"
                variant="outlined"
                helperText="This will appear on all payslips and official documents"
              />
            </Box>

            <Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Company Address
                </Typography>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={settings.company_address || ''}
                onChange={(e) => handleInputChange('company_address', e.target.value)}
                placeholder="Enter your complete company address"
                variant="outlined"
                helperText="Include street address, city, state/province, postal code, and country"
              />
            </Box>

            <Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  sx={{ minWidth: 120 }}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card elevation={1} sx={{ mt: 3, bgcolor: 'grey.50' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Preview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This is how your company information will appear on payslips:
          </Typography>
          <Box sx={{ p: 2, bgcolor: 'white', border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {settings.company_name || 'Your Company Name'}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {settings.company_address || 'Your Company Address'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default CompanySettings;